import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Store } from '@ngrx/store';
import { Subscription, take } from 'rxjs';

import { IAmountSpaces } from 'src/app/shared/interfaces/IAmountSpaces';
import { GuidedLoadingContext } from 'src/app/shared/interfaces/IGuidedLoadingContext';
import { IProcessOrder } from 'src/app/shared/interfaces/IProcessOrder';
import { LoadFlowOriginService } from 'src/app/shared/services/load-flow-origin/load-flow-origin.service';
import { OrderProcessingService } from 'src/app/shared/services/order-processing/order-processing.service';
import { DELIVERY_LIST_TYPE, SCAN_TYPE } from 'src/app/shared/utils/constants';
import { getOnCancelRedirectPath } from 'src/app/shared/utils/guided-loading.util';
import { Utils } from 'src/app/shared/utils/utils';
import { selectFrodoSettings } from 'src/app/stores/settings/settings.selector';
import { selectAvailableSpacesBySize } from 'src/app/stores/spaces';

@Component({
  templateUrl: './size-selector.component.html',
  styleUrls: ['./size-selector.component.scss'],
})
export class SizeSelectorComponent implements OnInit, OnDestroy {
  private routeData: GuidedLoadingContext;

  spaces: Record<number, IAmountSpaces> = {};
  logo = '';
  isLoading = false;
  frodoContext = '';
  selectedSize: IAmountSpaces | null = null;
  processOrder: IProcessOrder;
  inactivityTimeout;
  subscription: Subscription = new Subscription();

  constructor(
    private orderProcessingService: OrderProcessingService,
    private store: Store,
    private utils: Utils,
    private activatedRoute: ActivatedRoute,
    private loadFlowOrigin: LoadFlowOriginService
  ) {}

  ngOnInit(): void {
    this.routeData = this.activatedRoute.snapshot.data as GuidedLoadingContext;
    this.frodoContext = this.routeData.frodoContext;
    this.processOrder = this.orderProcessingService.orderToProcess;
    this.subscription.add(
      this.store.select(selectFrodoSettings).subscribe((frodo) => {
        this.logo = frodo.style.logo;
        this.getSpaces(frodo.timeouts.guidedOnloadOrder);
      })
    );
    this.subscription.add(
      this.utils.getLoadingValue().subscribe((value) => {
        this.isLoading = value;
      })
    );
  }

  getSpaces(timeout: number): void {
    this.subscription.add(
      this.store
        .select(selectAvailableSpacesBySize)
        .pipe(take(1))
        .subscribe((avaialableSpaces) => {
          this.spaces = Object.keys(avaialableSpaces).reduce(
            (acc: Record<string, IAmountSpaces>, size) => {
              if (!avaialableSpaces[size]?.spaces?.length) {
                return acc;
              }

              acc[size] = {
                spaces: avaialableSpaces[size].spaces,
                selected: false,
                label: `rearLoading.sizeSelector.sizeLabels.${size}`,
              };
              return acc;
            },
            {} as Record<number, IAmountSpaces>
          );

          this.inactivityTimeout = setTimeout(() => {
            this.cancelLoading();
          }, timeout);
        })
    );
  }

  getTargetScreen(): string {
    return getOnCancelRedirectPath(this.routeData.frodoContext, this.processOrder.type);
  }

  cancelLoading(): void {
    if (this.processOrder.type === DELIVERY_LIST_TYPE || this.processOrder.type === SCAN_TYPE) {
      this.utils.redirect(
        this.loadFlowOrigin.consumeExit(`${this.frodoContext}/${this.getTargetScreen()}`)
      );
      return;
    }
    this.utils.cancelLoading({
      frodoContext: this.frodoContext,
      targetScreen: this.getTargetScreen(),
      type: this.processOrder.type,
      flow: this.processOrder.flow,
      scheduleId: this.processOrder.scheduleId,
    });
  }

  onSizeSelected(size: IAmountSpaces) {
    this.selectedSize = size;
  }

  onNext(): void {
    if (!this.selectedSize) return;
    this.isLoading = true;
    const spaceBySize = this.selectedSize.spaces[0];

    const processOrder = {
      ...this.orderProcessingService.orderToProcess,
      size: spaceBySize.value.hardwareInfo.size,
    };

    this.subscription.add(
      this.utils.processOrderByActionType(processOrder).subscribe((response) => {
        processOrder.positions = [response.position];
        this.isLoading = false;
        this.utils.redirect(`${processOrder.frodoContext}/${this.routeData.paths.LOADING_ORDER}`, {
          type: processOrder.type,
          flow: processOrder.flow,
          scheduleId: processOrder.scheduleId,
        });
      })
    );
  }

  ngOnDestroy(): void {
    clearTimeout(this.inactivityTimeout);
    this.subscription.unsubscribe();
  }
}
